import React, { useMemo } from 'react';
import { Box, Spinner, SpaceBetween, Button } from '@amzn/awsui-components-core';
import { useConnectionsQuestionResponses } from '../useConnectionsData';
import { POS_COLOR, NEG_COLOR, RNA_COLOR, rollupByQuestion } from './questionResponseUtils';
import './question-sentiment-summary.scss';

interface Props {
  leaderLogin: string | null;
  metricPeriod: string;
  startDate: string;
  periodLabel?: string;
  /** Fired by the "View question-level breakdown" button — switch to Insights + scroll. */
  onViewDetail?: () => void;
}

const round = (v: number) => Math.round(v);

/**
 * Compact, volume-weighted Positive / Negative / RNA summary for the selected leader.
 * Aggregates response_count across ALL of the leader's questions into one 100%
 * stacked bar + KPI tiles, plus a "needs attention" teaser (top 3 questions by
 * negative %). Links to the full per-question breakdown in the Insights tab.
 *
 * Note: the leader's rows are already org-rolled-up upstream (LEADER_ID grain),
 * so this summarises the selected leader's whole org — it does not sum separate
 * per-direct-report scorecards.
 */
export const QuestionSentimentSummary: React.FC<Props> = ({ leaderLogin, metricPeriod, startDate, periodLabel, onViewDetail }) => {
  const { data, loading, error } = useConnectionsQuestionResponses(leaderLogin, metricPeriod, startDate || null);

  const agg = useMemo(() => {
    const questions = rollupByQuestion(data);
    let positive = 0;
    let negative = 0;
    let rna = 0;
    questions.forEach((q) => {
      positive += q.positive;
      negative += q.negative;
      rna += q.rna;
    });
    const total = positive + negative + rna;
    const topNegative = questions
      .map((q) => ({ ...q, negPct: q.total ? (q.negative / q.total) * 100 : 0 }))
      .sort((a, b) => b.negPct - a.negPct)
      .slice(0, 3)
      .filter((q) => q.negPct > 0);
    return { positive, negative, rna, total, questionCount: questions.length, topNegative };
  }, [data]);

  if (loading) {
    return (
      <Box textAlign="center" padding="xl">
        <Spinner size="big" /> Loading...
      </Box>
    );
  }
  if (error) {
    return (
      <Box color="text-status-error" padding="m">
        {error.message}
      </Box>
    );
  }
  if (agg.total === 0) {
    return (
      <Box color="text-body-secondary" padding="m">
        No question-level responses for this leader{periodLabel ? ` (${periodLabel})` : ''}.
      </Box>
    );
  }

  const pct = (v: number) => (agg.total ? (v / agg.total) * 100 : 0);
  const posPct = pct(agg.positive);
  const negPct = pct(agg.negative);
  const rnaPct = pct(agg.rna);

  const segment = (value: number, color: string, label: string) =>
    value > 0 ? (
      <div
        key={label}
        className="qss-seg"
        title={`${label}: ${round(pct(value))}% (${value.toLocaleString()})`}
        style={{
          width: `${pct(value)}%`,
          background: color,
          minWidth: pct(value) > 6 ? undefined : 0
        }}
      >
        {pct(value) > 8 ? `${round(pct(value))}%` : ''}
      </div>
    ) : null;

  const kpi = (label: string, value: number, color: string) => (
    <div className="qss-kpi">
      <div className="qss-kpi__value" style={{ color }}>
        {round(pct(value))}%
      </div>
      <div className="qss-kpi__label">{label}</div>
    </div>
  );

  return (
    <SpaceBetween size="m">
      {/* Overall 100% stacked bar (volume-weighted across all questions) */}
      <div>
        <div className="qss-bar">
          {segment(agg.positive, POS_COLOR, 'Positive')}
          {segment(agg.negative, NEG_COLOR, 'Negative')}
          {segment(agg.rna, RNA_COLOR, 'Neutral')}
        </div>
        <div className="qss-legend">
          <span>
            <span style={{ color: POS_COLOR }}>●</span> Positive
          </span>
          <span>
            <span style={{ color: NEG_COLOR }}>●</span> Negative
          </span>
          <span>
            <span style={{ color: RNA_COLOR }}>●</span> Neutral
          </span>
          <span className="qss-legend__spacer">
            {agg.questionCount} questions · {agg.total.toLocaleString()} responses
          </span>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="qss-kpis">
        {kpi('Positive', agg.positive, POS_COLOR)}
        {kpi('Negative', agg.negative, NEG_COLOR)}
        {kpi('Neutral', agg.rna, RNA_COLOR)}
      </div>

      {/* "Needs attention" teaser — top 3 questions by negative % */}
      {agg.topNegative.length > 0 && (
        <div>
          <div className="qss-attention__title">Needs attention — highest negative %</div>
          <SpaceBetween size="xs">
            {agg.topNegative.map((q) => (
              <div key={q.questionId} className="qss-attention__row">
                <div className="qss-attention__q" title={q.questionText}>
                  {q.questionText}
                </div>
                <div className="qss-attention__track">
                  <div className="qss-attention__fill" style={{ width: `${q.negPct}%`, background: NEG_COLOR }} />
                </div>
                <div className="qss-attention__pct" style={{ color: NEG_COLOR }}>
                  {round(q.negPct)}%
                </div>
              </div>
            ))}
          </SpaceBetween>
        </div>
      )}

      {onViewDetail && (
        <Box textAlign="right">
          <Button variant="link" iconName="angle-right" iconAlign="right" onClick={onViewDetail}>
            View question-level breakdown
          </Button>
        </Box>
      )}
    </SpaceBetween>
  );
};
